import React, { useState, useEffect, useMemo } from 'react';
import { Product } from '../App';
import { taxonomyAPI } from '../api';
import api from '../api';
import { formatCurrency } from '../utils/formatNumber';
import CollapsibleFilters from './CollapsibleFilters';
import SavedViews from './SavedViews';

interface Department {
  id: number;
  name: string;
  code: string;
}

interface Category {
  id: number;
  name: string;
  department_id: number;
}

interface SubCategory {
  id: number;
  name: string;
  category_id: number;
}

interface ProductListProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (id: number) => void;
  onBulkDelete: (ids: number[]) => void;
  currentPage: number;
  pageSize: number;
  totalProducts: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  userRole?: string;
  onSearch?: (searchTerm: string) => void;
  onFilterChange?: (filters: any) => void;
  onSelectionChange?: (ids: number[]) => void;
}

const ProductList: React.FC<ProductListProps> = ({
  products,
  onEdit,
  onDelete,
  onBulkDelete,
  currentPage,
  pageSize,
  totalProducts,
  onPageChange,
  onPageSizeChange,
  userRole = 'viewer',
  onSearch,
  onFilterChange,
  onSelectionChange
}) => {
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ status: '', brand: '', product_type: '', decoration: '', name: '', hsn_code: '' });
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [showImageCarousel, setShowImageCarousel] = useState(false);
  const [carouselImages, setCarouselImages] = useState<any[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Expanded variant families state
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());

  // Notify parent when selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedProducts);
    }
  }, [selectedProducts]);

  // ---- Persistent filters ----
  // Filters persist to localStorage so they survive reloads, and can be saved
  // as named "views" via the SavedViews control.
  const FILTERS_STORAGE_KEY = 'productListFilters';
  const readPersistedFilters = (): Record<string, any> => {
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };
  const persisted = readPersistedFilters();

  // Local filter state (seeded from persisted filters)
  const [searchTerm, setSearchTerm] = useState<string>(persisted.searchTerm || '');
  const [filterCategory, setFilterCategory] = useState<string>(persisted.filterCategory || '');
  const [filterStatus, setFilterStatus] = useState<string>(persisted.filterStatus || '');
  const [filterBrand, setFilterBrand] = useState<string>(persisted.filterBrand || '');
  const [filterChannel, setFilterChannel] = useState<string>(persisted.filterChannel || '');
  const [filterProductType, setFilterProductType] = useState<string>(persisted.filterProductType || '');

  // Debounced server-side search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);
  
  // Taxonomy filter state (seeded from persisted filters)
  const [filterDepartment, setFilterDepartment] = useState<string>(persisted.filterDepartment || '');
  const [filterSubcategory, setFilterSubcategory] = useState<string>(persisted.filterSubcategory || '');
  
  // Taxonomy data
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  
  // Channel data
  const [channels, setChannels] = useState<{id: number; name: string; type: string}[]>([]);

  // Collect all filter fields into a single serializable object.
  const collectFilters = (): Record<string, any> => ({
    searchTerm,
    filterCategory,
    filterStatus,
    filterBrand,
    filterChannel,
    filterProductType,
    filterDepartment,
    filterSubcategory,
  });

  // Apply a filter object (from a saved view) to local state, then propagate
  // the server-relevant filters to the parent.
  const applyFilters = (f: Record<string, any>) => {
    setSearchTerm(f.searchTerm || '');
    setFilterCategory(f.filterCategory || '');
    setFilterStatus(f.filterStatus || '');
    setFilterBrand(f.filterBrand || '');
    setFilterChannel(f.filterChannel || '');
    setFilterProductType(f.filterProductType || '');
    setFilterDepartment(f.filterDepartment || '');
    setFilterSubcategory(f.filterSubcategory || '');
    if (onFilterChange) {
      onFilterChange({
        channel_id: f.filterChannel || '',
        product_type: f.filterProductType || '',
      });
    }
    if (onSearch) onSearch(f.searchTerm || '');
  };

  // Persist filters to localStorage whenever they change so they survive reloads.
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(collectFilters()));
    } catch {
      /* ignore quota / serialization errors */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterCategory, filterStatus, filterBrand, filterChannel, filterProductType, filterDepartment, filterSubcategory]);

  // Load taxonomy and channel data on mount
  useEffect(() => {
    loadTaxonomyData();
    loadChannels();
  }, []);

  const loadChannels = async () => {
    try {
      const { channelAPI } = await import('../api');
      const res = await channelAPI.listChannels();
      setChannels(res.data || []);
    } catch (error) {
      console.error('Failed to load channels:', error);
    }
  };

  const loadTaxonomyData = async () => {
    try {
      const [deptResponse, catResponse, subcatResponse] = await Promise.all([
        taxonomyAPI.getDepartments(true),
        taxonomyAPI.getCategories(undefined, true),
        taxonomyAPI.getSubcategories(undefined, true)
      ]);
      
      setDepartments(deptResponse.data || []);
      setCategories(catResponse.data || []);
      setSubcategories(subcatResponse.data || []);
    } catch (error) {
      console.error('Failed to load taxonomy data:', error);
    }
  };

  // Get unique brands from products
  const brands = Array.from(new Set(products.map(p => p.brand).filter(Boolean)));
  const statuses = ['draft', 'active', 'inactive', 'archived'];

  // Decoration filter is client-side only (no server-side propagation needed)

  // Helper function to render taxonomy breadcrumb
  const renderTaxonomyBreadcrumb = (product: Product) => {
    const parts: string[] = [];
    
    if (product.department?.name) {
      parts.push(product.department.name);
    }
    if (product.category?.name) {
      parts.push(product.category.name);
    }
    if (product.subcategory?.name) {
      parts.push(product.subcategory.name);
    }
    
    if (parts.length === 0) {
      return <span style={{ color: '#6c757d', fontSize: '12px' }}>Uncategorized</span>;
    }
    
    return (
      <div style={{ fontSize: '11px', color: '#495057', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={parts.join(' > ')}>
        {parts.join(' > ')}
      </div>
    );
  };

  const totalPages = Math.ceil(totalProducts / pageSize);
  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalProducts);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedProducts(sortedProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedProducts([...selectedProducts, id]);
    } else {
      setSelectedProducts(selectedProducts.filter(pid => pid !== id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedProducts.length > 1) {
      setShowDeleteConfirm(true);
    } else if (selectedProducts.length === 1) {
      onBulkDelete(selectedProducts);
      setSelectedProducts([]);
    }
  };

  const confirmBulkDelete = () => {
    if (confirmText === 'confirm') {
      onBulkDelete(selectedProducts);
      setSelectedProducts([]);
      setShowDeleteConfirm(false);
      setConfirmText('');
    }
  };

  const handleBulkEdit = async () => {
    if (selectedProducts.length === 0) return;
    setBulkEditSaving(true);
    try {
      const updates: any = {};
      if (bulkEditData.status) updates.status = bulkEditData.status;
      if (bulkEditData.brand) updates.brand = bulkEditData.brand;
      if (bulkEditData.product_type) updates.product_type = bulkEditData.product_type;
      if (bulkEditData.name) updates.name = bulkEditData.name;
      
      // If decoration or HSN is set, include as attribute updates
      const decorationAttr = bulkEditData.decoration;
      const hsnValue = bulkEditData.hsn_code;

      let successCount = 0;
      let failCount = 0;
      for (const productId of selectedProducts) {
        try {
          const payload: any = { ...updates };
          const attrUpdates: any = {};
          
          if (decorationAttr) {
            const product = products.find(p => p.id === productId);
            const decoAttr = product?.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration');
            if (decoAttr) {
              attrUpdates[decoAttr.attribute_id] = decorationAttr;
            }
          }
          
          if (hsnValue) {
            // Find HSN attribute ID from product or fetch all attributes
            const product = products.find(p => p.id === productId);
            const hsnAttr = product?.attributes?.find(a => 
              a.attribute_name.toLowerCase() === 'hsn code' || 
              a.attribute_name.toLowerCase() === 'hsn' || 
              a.attribute_name.toLowerCase() === 'hsncode'
            );
            if (hsnAttr) {
              attrUpdates[hsnAttr.attribute_id] = hsnValue;
            }
          }
          
          if (Object.keys(attrUpdates).length > 0) {
            payload.attributes = attrUpdates;
          }
          
          await api.put(`/products/${productId}`, payload);
          successCount++;
        } catch (e) {
          failCount++;
        }
      }
      
      setShowBulkEdit(false);
      setBulkEditData({ status: '', brand: '', product_type: '', decoration: '', name: '', hsn_code: '' });
      setSelectedProducts([]);
      alert(`Bulk edit complete: ${successCount} updated${failCount > 0 ? `, ${failCount} failed` : ''}`);
      if (onSearch) onSearch(searchTerm);
    } catch (e) {
      alert('Bulk edit failed');
    } finally {
      setBulkEditSaving(false);
    }
  };

  const getStatusBadge = (status: string | null | undefined) => {
    // Handle null, undefined, or empty status
    const safeStatus = status || 'draft';
    return (
      <span className={`status-badge status-${safeStatus}`}>
        {safeStatus.charAt(0).toUpperCase() + safeStatus.slice(1)}
      </span>
    );
  };

  const openImageCarousel = (images: any[]) => {
    setCarouselImages(images);
    setCurrentImageIndex(0);
    setShowImageCarousel(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Client-side filtering (only for non-search filters that aren't handled server-side)
  const filteredProducts = products.filter(product => {
    if (filterDepartment && product.department_id?.toString() !== filterDepartment) return false;
    if (filterCategory && product.category_id?.toString() !== filterCategory) return false;
    if (filterSubcategory && product.subcategory_id?.toString() !== filterSubcategory) return false;
    if (filterStatus && product.status !== filterStatus) return false;
    if (filterBrand && product.brand !== filterBrand) return false;
    if (filterChannel) {
      const decoration = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value;
      if (decoration !== filterChannel) return false;
    }
    return true;
  });

  // Client-side sorting
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;

    switch (sortColumn) {
      case 'sku':
        return dir * (a.sku || '').localeCompare(b.sku || '');
      case 'name':
        return dir * (a.name || '').localeCompare(b.name || '');
      case 'decoration': {
        const aDec = a.attributes?.find(attr => attr.attribute_name.toLowerCase() === 'decoration')?.value || '';
        const bDec = b.attributes?.find(attr => attr.attribute_name.toLowerCase() === 'decoration')?.value || '';
        return dir * aDec.localeCompare(bDec);
      }
      case 'price':
        return dir * ((a.price || 0) - (b.price || 0));
      case 'brand':
        return dir * (a.brand || '').localeCompare(b.brand || '');
      case 'inventory':
        return dir * ((a.inventory_available || 0) - (b.inventory_available || 0));
      default:
        return 0;
    }
  });

  // Grouped products for variant family display
  type ProductGroup = {
    key: string;
    parent: Product;
    children: Product[];
    totalInventory: number;
    isGroup: boolean;
  };

  const groupedProducts = useMemo((): ProductGroup[] => {
    // Build a map of parent_id -> children
    const parentChildMap = new Map<number, Product[]>();
    const parentProducts = new Map<number, Product>();
    const standalone: Product[] = [];
    const childIds = new Set<number>();

    // First pass: identify all products that have parent_id set
    for (const product of sortedProducts) {
      if (product.parent_id) {
        const children = parentChildMap.get(product.parent_id) || [];
        children.push(product);
        parentChildMap.set(product.parent_id, children);
        childIds.add(product.id);
      }
    }

    // Second pass: identify parent products and standalone products
    for (const product of sortedProducts) {
      if (childIds.has(product.id)) continue; // Skip children (handled in groups)
      
      if (parentChildMap.has(product.id)) {
        // This product is a parent (has children referencing it)
        parentProducts.set(product.id, product);
      } else if (product.parent_id) {
        // Already counted as a child above
        continue;
      } else {
        // Check if it should be grouped by name
        standalone.push(product);
      }
    }

    // Group standalone products by name (products with identical names get grouped)
    const nameGroups = new Map<string, Product[]>();
    for (const product of standalone) {
      const key = product.name?.toLowerCase() || '';
      const group = nameGroups.get(key) || [];
      group.push(product);
      nameGroups.set(key, group);
    }

    const result: ProductGroup[] = [];

    // Add parent-child groups (ordered by first appearance in sortedProducts)
    const processedParentIds = new Set<number>();
    for (const product of sortedProducts) {
      // If this product is a parent with children
      if (parentChildMap.has(product.id) && !processedParentIds.has(product.id)) {
        processedParentIds.add(product.id);
        const children = parentChildMap.get(product.id) || [];
        const totalInventory = (product.inventory_available || 0) + 
          children.reduce((sum, c) => sum + (c.inventory_available || 0), 0);
        result.push({
          key: `parent-${product.id}`,
          parent: product,
          children,
          totalInventory,
          isGroup: true,
        });
      }
      // If this product has a parent_id but the parent isn't in sortedProducts
      // (parent might be on another page), group the orphaned children together
      if (product.parent_id && !parentProducts.has(product.parent_id) && !processedParentIds.has(product.parent_id)) {
        processedParentIds.add(product.parent_id);
        const children = parentChildMap.get(product.parent_id) || [];
        if (children.length > 1) {
          const totalInventory = children.reduce((sum, c) => sum + (c.inventory_available || 0), 0);
          result.push({
            key: `parent-${product.parent_id}`,
            parent: children[0],
            children: children.slice(1),
            totalInventory,
            isGroup: true,
          });
        } else if (children.length === 1) {
          result.push({
            key: `standalone-${children[0].id}`,
            parent: children[0],
            children: [],
            totalInventory: children[0].inventory_available || 0,
            isGroup: false,
          });
        }
      }
    }

    // Add name-grouped products
    const processedNames = new Set<string>();
    for (const product of sortedProducts) {
      if (childIds.has(product.id)) continue;
      if (product.parent_id) continue;
      if (parentChildMap.has(product.id)) continue;
      
      const key = product.name?.toLowerCase() || '';
      if (processedNames.has(key)) continue;
      processedNames.add(key);
      
      const group = nameGroups.get(key) || [];
      // Only group by name if at least one product is a variant type
      const hasVariantType = group.some(p => p.product_type === 'variant_parent' || p.product_type === 'variant_child');
      if (group.length > 1 && hasVariantType) {
        const totalInventory = group.reduce((sum, p) => sum + (p.inventory_available || 0), 0);
        result.push({
          key: `name-${key}`,
          parent: group[0],
          children: group.slice(1),
          totalInventory,
          isGroup: true,
        });
      } else {
        // Don't group — render each as standalone
        for (const p of group) {
          result.push({
            key: `standalone-${p.id}`,
            parent: p,
            children: [],
            totalInventory: p.inventory_available || 0,
            isGroup: false,
          });
        }
      }
    }

    return result;
  }, [sortedProducts]);

  const toggleFamily = (key: string) => {
    setExpandedFamilies(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <>
      <div className="card-header" style={{ padding: '12px 16px' }}>
        <h2 className="card-title" style={{ fontSize: '16px', fontWeight: '600' }}>Product Catalog</h2>
        <div style={{ fontSize: '12px', color: '#6c757d' }}>
          {sortedProducts.length} of {totalProducts} products
        </div>
      </div>

      {/* Compact Filter Section */}
      <CollapsibleFilters label="Filters">
      <div style={{ 
        padding: '12px 16px', 
        background: '#f8f9fa', 
        borderBottom: '1px solid #dee2e6',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '8px', flexWrap: 'wrap' }}>
          <SavedViews module="products" currentFilters={collectFilters()} onApply={applyFilters} />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => applyFilters({})}
            title="Clear all filters"
          >
            Clear filters
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Search
            </label>
            <input
              type="text"
              placeholder="SKU, name, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            />
          </div>
          
          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Department
            </label>
            <select
              value={filterDepartment}
              onChange={(e) => {
                setFilterDepartment(e.target.value);
                setFilterCategory('');
                setFilterSubcategory('');
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          
          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Category
            </label>
            <select
              value={filterCategory}
              onChange={(e) => {
                setFilterCategory(e.target.value);
                setFilterSubcategory('');
              }}
              disabled={!filterDepartment}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px',
                opacity: !filterDepartment ? 0.6 : 1
              }}
            >
              <option value="">All</option>
              {categories
                .filter(cat => !filterDepartment || cat.department_id.toString() === filterDepartment)
                .map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
            </select>
          </div>
          
          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              SubCategory
            </label>
            <select
              value={filterSubcategory}
              onChange={(e) => setFilterSubcategory(e.target.value)}
              disabled={!filterCategory}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px',
                opacity: !filterCategory ? 0.6 : 1
              }}
            >
              <option value="">All</option>
              {subcategories
                .filter(subcat => !filterCategory || subcat.category_id.toString() === filterCategory)
                .map(subcat => (
                  <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                ))}
            </select>
          </div>
          
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
          
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Brand
            </label>
            <select
              value={filterBrand}
              onChange={(e) => setFilterBrand(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Decoration
            </label>
            <select
              value={filterChannel}
              onChange={(e) => setFilterChannel(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {Array.from(new Set(products.map(p => p.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value).filter(Boolean))).sort().map(dec => (
                <option key={dec} value={dec}>{dec}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Product Type
            </label>
            <select
              value={filterProductType}
              onChange={(e) => {
                setFilterProductType(e.target.value);
                if (onFilterChange) onFilterChange({ product_type: e.target.value });
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              <option value="simple">Simple</option>
              <option value="variant_parent">Variant (Parent)</option>
              <option value="variant_child">Variant SKU (Child)</option>
              <option value="bundle">Bundle / Kit</option>
              <option value="customizable">Customizable</option>
              <option value="service">Service</option>
              <option value="virtual">Virtual</option>
            </select>
          </div>

          {(searchTerm || filterDepartment || filterCategory || filterSubcategory || filterStatus || filterBrand || filterChannel || filterProductType) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterDepartment('');
                setFilterCategory('');
                setFilterSubcategory('');
                setFilterStatus('');
                setFilterBrand('');
                setFilterChannel('');
                setFilterProductType('');
                if (onFilterChange) onFilterChange({ product_type: '' });
              }}
              style={{ 
                padding: '6px 12px',
                fontSize: '12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>
      </CollapsibleFilters>

      {selectedProducts.length > 0 && (
        <div style={{ padding: '8px 16px', background: '#fff3cd', borderBottom: '1px solid #ffc107', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>{selectedProducts.length} products selected</span>
          <button 
            className="btn btn-primary btn-sm" 
            style={{ padding: '4px 10px', fontSize: '12px' }}
            onClick={() => setShowBulkEdit(true)}
          >
            Bulk Edit
          </button>
          <button 
            className="btn btn-danger btn-sm" 
            style={{ padding: '4px 10px', fontSize: '12px' }}
            onClick={handleBulkDeleteClick}
          >
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        
        <table className="table" style={{ width: '100%', tableLayout: 'auto' }}>
          <colgroup>
            <col style={{ width: '36px' }} />
            <col style={{ width: '44px' }} />
            <col />
            <col />
            <col />
            <col style={{ width: '80px' }} />
            <col />
            <col style={{ width: '90px' }} />
            <col style={{ width: '120px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedProducts.length === sortedProducts.length && sortedProducts.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th></th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sku')}>SKU{getSortIndicator('sku')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{getSortIndicator('name')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('brand')}>Brand{getSortIndicator('brand')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('decoration')}>Decoration{getSortIndicator('decoration')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('price')}>Price{getSortIndicator('price')}</th>
              <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('inventory')}>Inventory{getSortIndicator('inventory')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupedProducts.map((group) => {
              const isExpanded = expandedFamilies.has(group.key);
              const { parent, children, totalInventory, isGroup } = group;

              if (!isGroup) {
                // Standalone product - render normally
                const product = parent;
                const isParentProduct = !product.is_variant && !product.parent_id;
                return (
                  <tr key={product.id} onClick={() => onEdit(product)} style={{ cursor: 'pointer' }}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={(e) => handleSelectProduct(product.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: '6px 8px', width: '40px' }}>
                      {product.images && product.images.length > 0 ? (
                        <img 
                          src={product.images[0]?.image_url} 
                          alt={product.name}
                          style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e8ecf0' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: '32px', height: '32px', borderRadius: '4px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#94a3b8' }}>□</div>
                      )}
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '150px' }} title={product.sku}>{product.sku}</td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '220px' }} title={product.name}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span 
                          onClick={() => onEdit(product)} 
                          style={{ cursor: 'pointer', color: '#0d9488', fontWeight: 500 }}
                        >
                          {product.name}
                        </span>
                      </div>
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '120px' }}>
                      {product.brand && product.brand !== product.name ? (
                        product.brand
                      ) : (
                        <span className="missing-data">Missing</span>
                      )}
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px', whiteSpace: 'nowrap', fontSize: '12px' }}>
                      {product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {product.price && product.price > 0 ? (
                        formatCurrency(product.price)
                      ) : (
                        <span className="missing-data">Missing</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <strong style={{ 
                        color: (product.inventory_available || 0) <= 0 ? '#dc2626' : 
                               (product.inventory_available || 0) <= 10 ? '#f59e0b' : '#16a34a',
                        fontSize: '13px'
                      }}>
                        {product.inventory_available ?? 0}
                      </strong>
                      {product.product_type === 'bundle' && <div style={{ fontSize: '9px', color: '#64748b' }}>kits</div>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {(userRole === 'admin' || userRole === 'manager') && (
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => onEdit(product)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            Edit
                          </button>
                        )}
                        {userRole === 'admin' && (
                          <button 
                            className="btn btn-danger" 
                            onClick={() => onDelete(product.id)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            Del
                          </button>
                        )}
                        {userRole === 'viewer' && (
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => onEdit(product)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }

              // Group row (parent/summary) + expanded children
              const variantCount = children.length;
              return (
                <React.Fragment key={group.key}>
                  {/* Summary row */}
                  <tr style={{ background: '#f8fafc' }}>
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedProducts.includes(parent.id)}
                        onChange={(e) => handleSelectProduct(parent.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: '6px 8px', width: '40px' }}>
                      {parent.images && parent.images.length > 0 ? (
                        <img 
                          src={parent.images[0]?.image_url} 
                          alt={parent.name}
                          style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e8ecf0' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ width: '32px', height: '32px', borderRadius: '4px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#94a3b8' }}>□</div>
                      )}
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '150px' }} title={parent.sku}>{parent.sku}</td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '220px' }} title={parent.name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span
                          onClick={() => toggleFamily(group.key)}
                          style={{ cursor: 'pointer', fontSize: '12px', userSelect: 'none', flexShrink: 0 }}
                          title={isExpanded ? 'Collapse variants' : 'Expand variants'}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <span 
                          onClick={() => onEdit(parent)} 
                          style={{ cursor: 'pointer', color: '#0d9488', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {parent.name}
                        </span>
                        <span style={{
                          background: '#e0e7ff',
                          color: '#3730a3',
                          fontSize: '11px',
                          fontWeight: 600,
                          padding: '1px 6px',
                          borderRadius: '10px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}>
                          {variantCount} variants
                        </span>
                      </div>
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '120px' }}>
                      {parent.brand && parent.brand !== parent.name ? (
                        parent.brand
                      ) : (
                        <span style={{ color: '#6b7280', fontSize: '11px' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px', whiteSpace: 'nowrap', fontSize: '12px' }}>
                      {parent.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {parent.price && parent.price > 0 ? (
                        formatCurrency(parent.price)
                      ) : (
                        <span style={{ color: '#6b7280', fontSize: '12px' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <strong style={{ 
                        color: totalInventory <= 0 ? '#dc2626' : 
                               totalInventory <= 10 ? '#f59e0b' : '#16a34a',
                        fontSize: '13px'
                      }}>
                        {totalInventory}
                      </strong>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>total</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        {(userRole === 'admin' || userRole === 'manager') && (
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => onEdit(parent)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            Edit
                          </button>
                        )}
                        {userRole === 'admin' && (
                          <button 
                            className="btn btn-danger" 
                            onClick={() => onDelete(parent.id)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            Del
                          </button>
                        )}
                        {userRole === 'viewer' && (
                          <button 
                            className="btn btn-secondary" 
                            onClick={() => onEdit(parent)}
                            style={{ padding: '4px 8px', fontSize: '12px' }}
                          >
                            View
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Expanded child rows */}
                  {isExpanded && children.map((child) => {
                    const colorAttr = child.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value;
                    const sizeAttr = child.attributes?.find(a => a.attribute_name.toLowerCase() === 'size')?.value;
                    const decorationAttr = child.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value;
                    return (
                      <tr key={child.id} style={{ background: '#fefefe', borderLeft: '3px solid #e0e7ff' }}>
                        <td>
                          <input
                            type="checkbox"
                            className="checkbox"
                            checked={selectedProducts.includes(child.id)}
                            onChange={(e) => handleSelectProduct(child.id, e.target.checked)}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', width: '40px' }}>
                          {child.images && child.images.length > 0 ? (
                            <img 
                              src={child.images[0]?.image_url} 
                              alt={child.name}
                              style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #e8ecf0' }}
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : (
                            <div style={{ width: '28px', height: '28px', borderRadius: '4px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#94a3b8' }}>□</div>
                          )}
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', paddingLeft: '24px', maxWidth: '150px', fontSize: '12px' }} title={child.sku}>
                          {child.sku}
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', paddingLeft: '24px', maxWidth: '220px' }}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px' }}>
                            <span 
                              onClick={() => onEdit(child)} 
                              style={{ cursor: 'pointer', color: '#0d9488' }}
                            >
                              {child.name}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>
                            {[
                              colorAttr && `Color: ${colorAttr}`,
                              sizeAttr && `Size: ${sizeAttr}`,
                            ].filter(Boolean).join(' · ') || 'Variant'}
                          </div>
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '8px', maxWidth: '120px', fontSize: '12px' }}>
                          {child.brand && child.brand !== child.name ? (
                            child.brand
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px', whiteSpace: 'nowrap', fontSize: '12px' }}>
                          {decorationAttr || <span style={{ color: '#94a3b8' }}>—</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>
                          {child.price && child.price > 0 ? (
                            formatCurrency(child.price)
                          ) : (
                            <span className="missing-data">Missing</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <strong style={{ 
                            color: (child.inventory_available || 0) <= 0 ? '#dc2626' : 
                                   (child.inventory_available || 0) <= 10 ? '#f59e0b' : '#16a34a',
                            fontSize: '12px'
                          }}>
                            {child.inventory_available ?? 0}
                          </strong>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {(userRole === 'admin' || userRole === 'manager') && (
                              <button 
                                className="btn btn-secondary" 
                                onClick={() => onEdit(child)}
                                style={{ padding: '3px 6px', fontSize: '11px' }}
                              >
                                Edit
                              </button>
                            )}
                            {userRole === 'admin' && (
                              <button 
                                className="btn btn-danger" 
                                onClick={() => onDelete(child.id)}
                                style={{ padding: '3px 6px', fontSize: '11px' }}
                              >
                                Del
                              </button>
                            )}
                            {userRole === 'viewer' && (
                              <button 
                                className="btn btn-secondary" 
                                onClick={() => onEdit(child)}
                                style={{ padding: '3px 6px', fontSize: '11px' }}
                              >
                                View
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Showing {startRecord} to {endRecord} of {totalProducts} products
        </div>
        
        <div className="pagination-controls">
          <span>Show:</span>
          <select 
            className="page-size-selector"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          
          <button 
            className="btn btn-secondary"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </button>
          
          <span>Page {currentPage} of {totalPages}</span>
          
          <button 
            className="btn btn-secondary"
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {showImageCarousel && (
        <div className="modal-overlay" onClick={() => setShowImageCarousel(false)}>
          <div className="modal" style={{ maxWidth: '80vw', maxHeight: '80vh' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>Image Gallery ({currentImageIndex + 1} of {carouselImages.length})</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowImageCarousel(false)}
                style={{ padding: '4px 8px' }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <img 
                src={carouselImages[currentImageIndex]?.image_url}
                alt={carouselImages[currentImageIndex]?.alt_text}
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '60vh', 
                  objectFit: 'contain',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onError={(e) => {
                  console.log('Image failed to load:', carouselImages[currentImageIndex]?.image_url);
                }}
              />
              
              {carouselImages.length > 1 && (
                <>
                  <button 
                    className="btn btn-primary"
                    onClick={prevImage}
                    style={{ 
                      position: 'absolute', 
                      left: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      padding: '8px 12px'
                    }}
                  >
                    ‹
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={nextImage}
                    style={{ 
                      position: 'absolute', 
                      right: '10px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      padding: '8px 12px'
                    }}
                  >
                    ›
                  </button>
                </>
              )}
            </div>
            
            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <p><strong>Alt Text:</strong> {carouselImages[currentImageIndex]?.alt_text || 'No alt text'}</p>
              <p><strong>Type:</strong> {carouselImages[currentImageIndex]?.image_type}</p>
            </div>
            
            {carouselImages.length > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '1rem' }}>
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: index === currentImageIndex ? '#007bff' : '#ddd',
                      cursor: 'pointer'
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <button 
              className="modal-close"
              onClick={() => {
                setShowDeleteConfirm(false);
                setConfirmText('');
              }}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedProducts.length} products. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Products
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkEdit && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <h3>Bulk Edit — {selectedProducts.length} Products</h3>
            <p style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>Only fields with a value will be updated. Leave blank to skip.</p>

            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Name</label>
                <input type="text" value={bulkEditData.name} onChange={(e) => setBulkEditData({ ...bulkEditData, name: e.target.value })} placeholder="Leave blank to skip" style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>HSN/GST Code</label>
                <input type="text" value={bulkEditData.hsn_code} onChange={(e) => setBulkEditData({ ...bulkEditData, hsn_code: e.target.value })} placeholder="Leave blank to skip" style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Status</label>
                <select value={bulkEditData.status} onChange={(e) => setBulkEditData({ ...bulkEditData, status: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="">— No change —</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="review">Review</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Brand</label>
                <input type="text" value={bulkEditData.brand} onChange={(e) => setBulkEditData({ ...bulkEditData, brand: e.target.value })} placeholder="Leave blank to skip" style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Product Type</label>
                <select value={bulkEditData.product_type} onChange={(e) => setBulkEditData({ ...bulkEditData, product_type: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="">— No change —</option>
                  <option value="simple">Simple</option>
                  <option value="variant_parent">Variant (Parent)</option>
                  <option value="variant_child">Variant SKU (Child)</option>
                  <option value="bundle">Bundle / Kit</option>
                  <option value="customizable">Customizable</option>
                  <option value="service">Service</option>
                  <option value="virtual">Virtual</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Decoration</label>
                <input type="text" value={bulkEditData.decoration} onChange={(e) => setBulkEditData({ ...bulkEditData, decoration: e.target.value })} placeholder="e.g., Printing, Embroidery" style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '20px' }}>
              <button className="btn btn-cancel" onClick={() => { setShowBulkEdit(false); setBulkEditData({ status: '', brand: '', product_type: '', decoration: '', name: '', hsn_code: '' }); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkEdit} disabled={bulkEditSaving || (!bulkEditData.status && !bulkEditData.brand && !bulkEditData.product_type && !bulkEditData.decoration && !bulkEditData.name && !bulkEditData.hsn_code)}>
                {bulkEditSaving ? 'Updating...' : 'Apply Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductList;